/**
 * Types for managing draggable section ordering in the character sheet
 */

export interface SectionOrderPreferences {
  userId: string;
  sectionOrder: string[];
  columnAssignment?: {
    left: string[];
    right: string[];
  };
  lastModified: number;
}

export interface CollapsibleStatePreferences {
  collapsibleStates: Record<string, boolean>;
  lastModified: number;
}

export interface DraggableSection {
  id: string;
  title: string;
  component: React.ComponentType<any>;
  defaultExpanded: boolean;
  priority: 'high' | 'medium' | 'low';
  order: number;
  column?: 'left' | 'right';
}

export interface SectionOrderContextType {
  sectionOrder: string[];
  setSectionOrder: (order: string[]) => void;
  columnAssignment: {
    left: string[];
    right: string[];
  };
  setColumnAssignment: (assignment: { left: string[]; right: string[] }) => void;
  // Collapsible state management
  collapsibleStates: Record<string, boolean>;
  setSectionCollapsed: (sectionId: string, collapsed: boolean) => void;
  setAllSectionsCollapsed: (collapsed: boolean) => void;
  resetToDefault: () => void;
  isLoading: boolean;
}

// Default section order for new users
export const DEFAULT_SECTION_ORDER = [
  'character-traits',
  'basic-info', 
  'equipped-items',
  'domain-abilities',
  'domain-spells',
  'experiences',
  'inventory'
];

// Default column assignment strategy
export const DEFAULT_COLUMN_ASSIGNMENT = {
  left: ['character-traits', 'basic-info', 'domain-abilities', 'experiences'],
  right: ['equipped-items', 'domain-spells', 'inventory']
};

// Default collapsed states for new characters (all collapsed)
export const DEFAULT_COLLAPSIBLE_STATES: Record<string, boolean> = {
  'character-traits': false,
  'basic-info': false,
  'equipped-items': false,
  'domain-abilities': false,
  'domain-spells': false,
  'experiences': false,
  'inventory': false
};

// Helper to initialize column assignment from section order
export const createColumnAssignmentFromOrder = (sectionOrder: string[]) => {
  const left: string[] = [];
  const right: string[] = [];
  
  sectionOrder.forEach((id, index) => {
    if (index % 2 === 0) {
      left.push(id);
    } else {
      right.push(id);
    }
  });
  
  return { left, right };
};